import { useMemo } from 'react';

import { useAnalyticsAttributes } from '../store/bootstrap';
import { useSearchExperience } from '../store/search';
import { useCommonAttributeInputs } from './use-common-attribute-inputs';
import { getAttributes } from './utils';

/**
 * Attributes shared by multiple result events. Event payload ownership remains in the event hook's
 * third argument; list-wide values may be transported here and routed there at the row boundary.
 * Interaction-time attributes belong in the returned callback.
 *
 * @see ./README.md
 */
export type SharedEventAttributes = ReturnType<typeof getAttributes>;
/** Analytics data prepared once and shared by the result rows. */
export type PreparedResultAttributes = SharedEventAttributes & {
	queryUpdatedTime?: number;
};

/** Shared derivation is owned by one mounted list, never by a global cache. */
export const usePreparedResultAnalytics = (): PreparedResultAttributes => {
	const [analyticsAttributes] = useAnalyticsAttributes();
	const [experience] = useSearchExperience();
	const inputs = useCommonAttributeInputs({ analyticsAttributes, experience });

	return useMemo(
		() => ({
			...getAttributes({ ...inputs, queryUpdatedTime: undefined }),
			queryUpdatedTime: inputs.queryUpdatedTime,
		}),
		[inputs],
	);
};

// TODO: https://product-fabric.atlassian.net/browse/QS-10797
// Standardise queryUpdatedTime sources and queryUpdatedAgo capture points.
/** Keep the capture point at each consuming render, including local row updates. */
export const getResultAttributesAtRender = ({
	commonAttributes,
	nonPrivacySafeAttributes,
	queryUpdatedTime,
}: PreparedResultAttributes): SharedEventAttributes => ({
	commonAttributes: {
		...commonAttributes,
		queryUpdatedAgo: queryUpdatedTime ? Date.now() - queryUpdatedTime : undefined,
	},
	nonPrivacySafeAttributes,
});
